// Initialize DataDog
import { LogsEvent, datadogLogs } from '@datadog/browser-logs';
import { RumEvent, datadogRum } from '@datadog/browser-rum';
import { User } from './types';
import { scrubUrl } from './utils/telemetry';

// Every field is guarded rather than wrapped: a throw would be swallowed by the SDK, which then ships
// the event anyway, so the only useful defence is not throwing.
export const scrubLogsEvent = (log: LogsEvent): boolean => {
  if (log.view) {
    log.view.url = scrubUrl(log.view.url);
    if (log.view.referrer) log.view.referrer = scrubUrl(log.view.referrer);
  }
  if (log.http?.url) log.http.url = scrubUrl(log.http.url);
  // Network-origin logs put the whole request URL in the message, not only in http.url.
  if (log.message) log.message = scrubUrl(log.message);
  if (log.error) {
    // For a rejected or 5xx request this stack is the response body, which can echo the request URI.
    if (log.error.stack) log.error.stack = scrubUrl(log.error.stack);
    if (log.error.message) log.error.message = scrubUrl(log.error.message);
  }

  return true;
};

export const scrubRumEvent = (event: RumEvent): boolean => {
  if (event.view) {
    event.view.url = scrubUrl(event.view.url);
    // Bigger channel than view.url in production, and the only one reaching error events.
    if (event.view.referrer) event.view.referrer = scrubUrl(event.view.referrer);
  }
  if (event.type === 'resource' && event.resource) event.resource.url = scrubUrl(event.resource.url);
  if (event.type === 'error' && event.error) {
    event.error.message = scrubUrl(event.error.message);
    if (event.error.stack) event.error.stack = scrubUrl(event.error.stack);
    // Unpopulated by this SDK version, but allow-listed — so it would start carrying URLs unannounced.
    if (event.error.resource) event.error.resource.url = scrubUrl(event.error.resource.url);
  }

  return true;
};

const initializeDataDog = () => {
  // Automated browsers (crawlers, e2e) burn RUM/replay quota and report the SDK's own aborted uploads as errors.
  if (navigator.webdriver || /HeadlessChrome/.test(navigator.userAgent)) return;

  const {
    REACT_APP_DATADOG_CLIENT_TOKEN,
    REACT_APP_DATADOG_RUM_APP_ID,
    REACT_APP_DATADOG_RUM_CLIENT_TOKEN,
    REACT_APP_DATADOG_SITE,
    REACT_APP_DATADOG_ENV
  } = process.env;

  if (REACT_APP_DATADOG_CLIENT_TOKEN && REACT_APP_DATADOG_SITE) {
    datadogLogs.init({
      clientToken: REACT_APP_DATADOG_CLIENT_TOKEN as string,
      site: REACT_APP_DATADOG_SITE as string,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: 'upstack-web',
      env: REACT_APP_DATADOG_ENV as string,
      beforeSend: scrubLogsEvent
    });
  }

  if (REACT_APP_DATADOG_RUM_APP_ID && REACT_APP_DATADOG_RUM_CLIENT_TOKEN && REACT_APP_DATADOG_SITE) {
    datadogRum.init({
      applicationId: REACT_APP_DATADOG_RUM_APP_ID as string,
      clientToken: REACT_APP_DATADOG_RUM_CLIENT_TOKEN as string,
      site: REACT_APP_DATADOG_SITE as string,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      service: 'upstack-web',
      defaultPrivacyLevel: 'mask-user-input',
      // Without this, data-dd-privacy is ignored when naming click actions — masking there is opt-in.
      enablePrivacyForActionName: true,
      env: REACT_APP_DATADOG_ENV as string,
      beforeSend: scrubRumEvent
    });

    datadogRum.startSessionReplayRecording();
  }
};

export default initializeDataDog;
export const setDataDogUser = (user: User) => {
  datadogRum.setUser({
    id: user.id,
    name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    account: user.account_name,
    accountId: user.account_id,
    role: user.role
  });
};

// setUser survives sign-out, so without this the next events still carry the previous identity.
export const clearDataDogUser = () => datadogRum.clearUser();
